import { nameInitials } from '@/components/enc/format';
import { InitialsAvatar } from '@/components/enc/primitives';
import { usePathname } from '@/hooks/use-pathname';
import { Roles } from '@/lib/selectors/roles';
import { cn } from '@/lib/utils';
import { useTheme } from '@/providers/theme/use-theme';
import { useCurrentUser, useSignOut } from '@/services/auth';
import { useCouples } from '@/services/couples';
import { useEvents } from '@/services/events';
import { useUsers } from '@/services/users';
import { Calendar, LayoutGrid, LogOut, ShieldCheck, UserRound, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface NavItem {
  label: string;
  url: string;
  icon: ReactNode;
  count?: number;
}

interface AppSidebarProps {
  open: boolean;
  onNavigate: () => void;
}

export function AppSidebar({ open, onNavigate }: AppSidebarProps) {
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const { data: user } = useCurrentUser();
  const { mutate: signOut } = useSignOut();

  const { data: couples } = useCouples({ perPage: 1 });
  const { data: events } = useEvents({ perPage: 1 });
  const { data: users } = useUsers({ perPage: 1 });

  const sections: { title: string; items: NavItem[] }[] = [
    {
      title: 'GERAL',
      items: [{ label: 'Dashboard', url: '/admin', icon: <LayoutGrid /> }],
    },
    {
      title: 'CADASTROS',
      items: [
        {
          label: 'Casais',
          url: '/admin/couples',
          icon: <Users />,
          count: couples?.pagination.total,
        },
        {
          label: 'Encontros',
          url: '/admin/events',
          icon: <Calendar />,
          count: events?.pagination.total,
        },
      ],
    },
    {
      title: 'CONFIGURAÇÕES',
      items: [
        {
          label: 'Usuários',
          url: '/admin/users',
          icon: <ShieldCheck />,
          count: users?.pagination.total,
        },
        { label: 'Meu perfil', url: '/admin/profile', icon: <UserRound /> },
      ],
    },
  ];

  const isActive = (url: string) =>
    url === '/admin'
      ? pathname === '/admin' || pathname === '/admin/'
      : pathname === url || pathname.startsWith(`${url}/`);

  return (
    <aside
      className={cn(
        'top-0 left-0 z-40 flex h-screen w-[248px] flex-none flex-col gap-7 overflow-y-auto border-r border-line bg-surface px-3.5 py-5',
        'sticky max-[999px]:fixed max-[999px]:transition-transform max-[999px]:duration-300',
        open ? 'max-[999px]:translate-x-0' : 'max-[999px]:-translate-x-full',
      )}
    >
      <Link
        to="/admin"
        onClick={onNavigate}
        className="flex items-center gap-3 px-2 py-1"
      >
        <span className="flex size-[38px] items-center justify-center rounded-[10px] border border-line-2">
          <img
            src="/assets/logo.png"
            alt="Comunidade Católica Apóstolos"
            className="logo-img h-[22px] w-auto"
          />
        </span>
        <span className="flex flex-col gap-[3px]">
          <span className="font-display text-[17px] leading-none font-extrabold tracking-[-0.02em] text-ink">
            Enchei-vos
          </span>
          <span className="text-[10px] leading-none font-bold tracking-[.12em] text-muted">
            ADMIN
          </span>
        </span>
      </Link>

      <nav className="flex flex-1 flex-col gap-[22px]">
        {sections.map((section) => (
          <div key={section.title} className="flex flex-col gap-1">
            <span className="px-2.5 pb-1.5 text-[10px] font-bold tracking-[.12em] text-muted">
              {section.title}
            </span>
            {section.items.map((item) => {
              const active = isActive(item.url);
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  onClick={onNavigate}
                  className={cn(
                    'flex h-10 items-center gap-3 rounded-[10px] px-2.5 text-sm transition-colors [&_svg]:size-[18px]',
                    active
                      ? 'bg-brand-tint font-semibold text-brand'
                      : 'font-medium text-body hover:bg-surface-2',
                  )}
                >
                  {item.icon}
                  <span className="flex-1">{item.label}</span>
                  {item.count !== undefined && (
                    <span className="text-[11px] font-bold text-muted">{item.count}</span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 px-1">
          <span className="px-1.5 text-[10px] font-bold tracking-[.12em] text-muted">
            TEMA
          </span>
          <div className="grid h-[42px] grid-cols-2 gap-1 rounded-full border border-line-2 p-1">
            {(['light', 'dark'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTheme(t)}
                className={cn(
                  'rounded-full text-[13px] font-semibold transition-colors',
                  theme === t ? 'bg-brand-fill text-white' : 'text-body hover:text-ink',
                )}
              >
                {t === 'light' ? 'Claro' : 'Escuro'}
              </button>
            ))}
          </div>
        </div>

        {user && (
          <div
            className={cn(
              'flex items-center gap-2.5 rounded-[12px] border p-2.5 transition-colors',
              isActive('/admin/profile') ? 'border-brand bg-brand-tint' : 'border-line',
            )}
          >
            <Link
              to="/admin/profile"
              onClick={onNavigate}
              className="flex min-w-0 flex-1 items-center gap-2.5"
              title="Meu perfil"
            >
              <InitialsAvatar
                text={nameInitials(user.name)}
                src={user.avatar}
                className="size-[34px] text-[13px]"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[13px] font-semibold text-ink">
                  {user.name}
                </span>
                <span className="text-[11px] text-muted">{Roles[user.role]}</span>
              </span>
            </Link>
            <button
              type="button"
              title="Sair"
              onClick={() => signOut()}
              className="flex text-muted transition-colors hover:text-brand"
            >
              <LogOut className="size-[18px]" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
